/*******************************************************************************
  SiteConfigForm.js

  Author: Mark Duckworth; Advait;
*******************************************************************************/
import React, { useState } from "react";
import { useSelector } from "react-redux";
import { selectSite } from "../store/siteSlice";

import "../index.css";

/**
 * This component is a form for editing Site object field values.
 * This follows the "controlled components" pattern in React.
 * https://reactjs.org/docs/forms.html#controlled-components
 */
function SiteConfigForm(props) {
  const isSysAdmin = useSelector((state) => state.user.sysAdmin);
  const selectedSite = useSelector(selectSite);
  const [form, setForm] = useState({
    ...selectedSite,
    siteid: selectedSite.id,
  });
  const [saveButtonDisabled, setSaveButtonDisabled] = useState(true);

  const timezones = [
    "Europe/Paris",
    "Europe/London",
    "America/Sao_Paulo",
    "America/New_York",
    "America/Chicago",
    "America/Denver",
    "America/Los_Angeles",
    "Asia/Tokyo",
    "Asia/Singapore",
    "Asia/Kolkata",
    "Asia/Dubai",
  ];

  // keep state up to date whenever the user edits values in the form
  function handleInputChange(event) {
    setSaveButtonDisabled(false);
    const target = event.target;
    setForm((prev) => {
      const newState = { ...prev };
      newState[target.name] = target.value;
      return newState;
    });
  }

  /**
   * Create a valid "restrict_ui_page" dict if it doesn't exist.
   * Return new dict, with values from the restrctui parameter,
   * or default values.
   * @param {dict} restrctui
   * @returns new dict
   */
  function verifyRestrictUIPageFields(restrctui) {
    var new_restrict_ui = {
      inspection: false,
      reception: false,
      security_patrol: false,
    };
    if (restrctui) {
      Object.assign(new_restrict_ui, restrctui);
    }
    return new_restrict_ui;
  }

  function handleRestrictUI(e, uiPage) {
    setSaveButtonDisabled(false);
    var new_restrictui = verifyRestrictUIPageFields(form.restrict_ui_page);
    new_restrictui[uiPage] = e.target.checked;
    setForm((prev) => {
      const newState = { ...prev };
      newState.restrict_ui_page = new_restrictui;
      return newState;
    });
  }

  // Handle checkbox changes for monitoring settings
  function handleCheckboxChange(event) {
    setSaveButtonDisabled(false);
    const target = event.target;
    setForm((prev) => {
      const newState = { ...prev };
      newState[target.name] = target.checked;
      return newState;
    });
  }
  /**
   * Validate recharge threshold values and return message if any issues detected.
   * @returns string with validation message, or null if no issues
   */
  function getRechargeValidationMessage() {
    const currentMinRecharge = Number(form.recharge_threshold_min);
    const currentMaxRecharge = Number(form.recharge_threshold_max);
    // check min recharge is a number and at least 15
    if (!Number.isFinite(currentMinRecharge) || currentMinRecharge < 15) {
      return "Warning: Min Recharge should be at least 15%. Please update for proper functioning.";
    }
    // check max recharge is a number and at least 5% greater than min recharge
    if (!Number.isFinite(currentMaxRecharge) || currentMaxRecharge < currentMinRecharge + 5) {
      return "Warning: Max Recharge should be at least 5% greater than Min Recharge. Please update for proper functioning.";
    }

    return null;
  }

  // user clicked the save button
  async function handleSubmit(event) {
    event.preventDefault();

    const rechargeValidationMessage = getRechargeValidationMessage();
    if (rechargeValidationMessage) {
      alert(rechargeValidationMessage);
    }

    setSaveButtonDisabled(true);
    props.onSave(form); // notify parent we saved new data
  }

  // sysadmin config section
  function AdminSection() {
    const restrict_ui = form.restrict_ui_page;
    const hideInspectionPage = Boolean(restrict_ui && restrict_ui.inspection);
    const hideReceptionPage = Boolean(restrict_ui && restrict_ui.reception);
    const hideSecurityPatrolPage = Boolean(restrict_ui && restrict_ui.security_patrol);
    const timezoneOptions = [...timezones];
    if (form.timezone && !timezoneOptions.includes(form.timezone)) {
      timezoneOptions.unshift(form.timezone);
    }
    return (
      <>
        <tr>
          <td
            className="individualContainerTitleColumn"
            style={{ verticalAlign: "baseline", paddingTop: "15px" }}
          >
            <h4 className="individualContainerTitleHeading">RMS Information</h4>
          </td>
          <td>
            <table>
              <tbody>
                <tr style={{ verticalAlign: "top" }}>
                  <td className="individualContainerSubTitleColumn">
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <h6 style={{ paddingTop: "5px" }}>Domain</h6>
                      <span style={{ fontSize: "smaller", color: "#8D969E" }}>
                        (e.g. {`<ID>.ava8.net`})
                      </span>
                    </div>
                  </td>
                  <td style={{ verticalAlign: "top" }}>
                    <input
                      className="inputField"
                      type="text"
                      name="rmsName"
                      size="40"
                      value={form.rmsName || "<rms>.ava8.net"}
                      onChange={handleInputChange}
                    ></input>
                  </td>
                </tr>
                <tr>
                  <td className="individualContainerSubTitleColumn">
                    <h6>Username</h6>
                  </td>
                  <td>
                    <input
                      className="inputField"
                      type="text"
                      name="rmsUser"
                      size="40"
                      value={form.rmsUser}
                      onChange={handleInputChange}
                    ></input>
                  </td>
                </tr>
                <tr>
                  <td className="individualContainerSubTitleColumn">
                    <h6>Password</h6>
                  </td>
                  <td>
                    <input
                      className="inputField"
                      type="password"
                      name="rmsPw"
                      size="40"
                      value={form.rmsPw}
                      onChange={handleInputChange}
                    ></input>
                  </td>
                </tr>
                <tr>
                  <td className="individualContainerSubTitleColumn">
                    <h6>Map Name</h6>
                  </td>
                  <td>
                    <input
                      className="inputField"
                      type="text"
                      name="mapName"
                      size="40"
                      value={form.mapName}
                      onChange={handleInputChange}
                    ></input>
                  </td>
                </tr>
              </tbody>
            </table>
          </td>
        </tr>
        <tr>
          <td className="individualContainerTitleColumn" valign="top">
            <h4 className="individualContainerTitleHeading">Other Site Settings</h4>
          </td>
          <td>
            <table>
              <tbody>
                <tr>
                  <td className="individualContainerSubTitleColumn">
                    <h6>Site Name</h6>
                  </td>
                  <td>
                    <input
                      className="inputField"
                      type="text"
                      name="siteName"
                      size="40"
                      value={form.siteName}
                      onChange={handleInputChange}
                    ></input>
                  </td>
                </tr>
                <tr>
                  <td className="individualContainerSubTitleColumn">
                    <h6>
                      <a
                        href="https://en.wikipedia.org/wiki/List_of_tz_database_time_zones"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Timezone
                      </a>
                    </h6>
                  </td>
                  <td>
                    <select
                      className="inputField"
                      name="timezone"
                      value={form.timezone}
                      onChange={handleInputChange}
                    >
                      {timezoneOptions.map((tz) => (
                        <option key={tz} value={tz}>
                          {tz}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
                <tr>
                  <td className="individualContainerSubTitleColumn">
                    <h6>Min Recharge</h6>
                  </td>
                  <td>
                    <input
                      className="inputField"
                      style={{ width: "50px" }}
                      type="number"
                      min="0"
                      max="100"
                      name="recharge_threshold_min"
                      size="40"
                      value={form.recharge_threshold_min}
                      onChange={handleInputChange}
                    ></input>{" "}
                    %{" "}
                  </td>
                </tr>
                <tr>
                  <td className="individualContainerSubTitleColumn">
                    <h6>Max Recharge</h6>
                  </td>
                  <td>
                    <input
                      className="inputField"
                      style={{ width: "50px" }}
                      type="number"
                      min="0"
                      max="100"
                      name="recharge_threshold_max"
                      size="40"
                      value={form.recharge_threshold_max}
                      onChange={handleInputChange}
                    ></input>{" "}
                    %{" "}
                  </td>
                </tr>
              </tbody>
            </table>
          </td>
        </tr>
        <tr>
          <td className="individualContainerTitleColumn" valign="top">
            <h4 className="individualContainerTitleHeading">Restrict UI</h4>
          </td>
          <td>
            <table>
              <tbody>
                <tr>
                  <td className="siteConfigCheckboxSubTitleColumn">
                    <h6 className="siteConfigCheckboxSubTitleColumnH6">
                      Restrict Inspection UI
                    </h6>
                  </td>
                  <td valign="top">
                    <input
                      onChange={(e) => handleRestrictUI(e, "inspection")}
                      name="restrictInspectionUI"
                      type="checkbox"
                      label="Restrict Inspection UI"
                      checked={hideInspectionPage}
                    />
                  </td>
                </tr>
                <tr>
                  <td className="siteConfigCheckboxSubTitleColumn">
                    <h6 className="siteConfigCheckboxSubTitleColumnH6">
                      Restrict Reception UI{" "}
                    </h6>
                  </td>
                  <td valign="top">
                    <input
                      onChange={(e) => handleRestrictUI(e, "reception")}
                      name="retrictReceptionUI"
                      type="checkbox"
                      label="Restrict Reception UI"
                      checked={hideReceptionPage}
                    />
                  </td>
                </tr>
                <tr>
                  <td className="siteConfigCheckboxSubTitleColumn">
                    <h6 className="siteConfigCheckboxSubTitleColumnH6">
                      Restrict Security Patrol UI{" "}
                    </h6>
                  </td>
                  <td valign="top">
                    <input
                      onChange={(e) => handleRestrictUI(e, "security_patrol")}
                      name="restrictSecurityPatrolUI"
                      type="checkbox"
                      label="Restrict Security Patrol UI"
                      checked={hideSecurityPatrolPage}
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </td>
        </tr>
      </>
    );
  }

  return (
    <div className="individualContainer">
      <form onSubmit={handleSubmit}>
        <table className="individualContainerTable">
          <tbody>
            <tr>
              <td className="individualContainerTitleColumn" valign="top">
                <h4 className="individualContainerTitleHeading">Emails for Notifications</h4>
              </td>
              <td>
                <table>
                  <tbody>
                    <tr>
                      <td
                        className="individualContainerSubTitleColumn"
                        style={{ paddingTop: "8px", verticalAlign: "top" }}
                      >
                        <div style={{ display: "flex", flexDirection: "column" }}>
                          <h6>Addresses</h6>
                          <span
                            style={{
                              fontSize: "smaller",
                              color: "#8D969E",
                              width: "190px",
                            }}
                          >
                            (Enter multiple emails comma-separated if applicable)
                          </span>
                        </div>
                      </td>
                      <td>
                        <textarea
                          className="inputField"
                          type="text"
                          style={{ minHeight: "90px" }}
                          name="emailsToNotify"
                          size="40"
                          value={form.emailsToNotify}
                          onChange={handleInputChange}
                        />
                      </td>
                    </tr>
                    {isSysAdmin && (
                      <tr>
                        <td
                          className="individualContainerSubTitleColumn"
                          style={{ verticalAlign: "top" }}
                        >
                          <h6>Sysadmin Emails</h6>
                        </td>
                        <td>
                          <textarea
                            className="inputField"
                            type="text"
                            style={{ minHeight: "90px" }}
                            name="adminEmailsToNotify"
                            size="40"
                            value={form.adminEmailsToNotify}
                            onChange={handleInputChange}
                          />
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </td>
            </tr>
            {isSysAdmin && AdminSection()}
            <tr>
              <td className="individualContainerTitleColumn" valign="top">
                <h4 className="individualContainerTitleHeading">Monitoring Settings</h4>
              </td>
              <td>
                <table>
                  <tbody>
                    <tr>
                      <td className="siteConfigCheckboxSubTitleColumn">
                        <h6 className="siteConfigCheckboxSubTitleColumnH6">
                          Enable Passive Monitoring
                        </h6>
                      </td>
                      <td valign="top">
                        <input
                          onChange={handleCheckboxChange}
                          name="enablemonitoringdata"
                          type="checkbox"
                          label="Enable Passive Monitoring"
                          checked={form.enablemonitoringdata || false}
                        />
                      </td>
                    </tr>
                    <tr>
                      <td className="siteConfigCheckboxSubTitleColumn">
                        <h6 className="siteConfigCheckboxSubTitleColumnH6">
                          Allow Image Store in Ava Cloud
                        </h6>
                      </td>
                      <td valign="top">
                        <input
                          onChange={handleCheckboxChange}
                          name="allow_image_store"
                          type="checkbox"
                          label="Allow Image Store in Ava Cloud"
                          checked={form.allow_image_store || false}
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </td>
            </tr>
            <tr>
              <td className="individualContainerTitleColumn" valign="top"></td>
              <td style={{ padding: "15px 0" }}>
                <input
                  className="saveButton"
                  type="submit"
                  disabled={saveButtonDisabled}
                  value="Save"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </form>
    </div>
  );
  //}
}
export default SiteConfigForm;
